import type { UseStepperReturn } from '@vueuse/core';
import {
  type FormContext,
} from 'vee-validate';
import { type IBookingFormDated } from '@/components/tripBookingPage/datedForm/datedBookingForm';
import type {
  AnyObject,
  FormItemConfig,
  FormItemConfigRaw,
  FormSubmissionHandler,
} from '@/types';
import type { IBookingFormTemplate } from '@/components/tripBookingPage/templateForm/templateForm.config';
import type { ComposerTranslation } from 'vue-i18n';

/** Propriétés passées à l'enregistreur de formulaire */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type BookingStepForm<T extends AnyObject = any> = Pick<FormContext<T>, 'values' | 'resetForm' | 'validate'> & {
  /** Le id du formulaire. Il est alors possible de retrouver un formulaire existant par la suite. */
  name: string;
  /**
   * Indique si le formulaire a été modifié depuis son état initial.
   * Exposé comme fonction (et non comme `ComputedRef`) pour éviter des erreurs circulaires quand les valeurs sont
   * stockées.
   */
  isDirty?: () => boolean;
};

/**
 * Propriétés exposées dans la composante StepForm
 */
export type StepFormExposedProps<TVal extends AnyObject> = {
  resetForm: BookingStepForm<TVal>['resetForm'];
};

/** Événements communs à tous les sous-formulaires */
export type BookingFormStepEmitters = {
  previous: [];
  init: [formData: BookingStepForm];
  error: [];
};

/**
 * Propriétés associées comme valeur à une "étape", c'est à dire le nom unique attribué à `useStepper` qui représente
 * une page du formulaire.
 *
 * Optionnellement, l'objet de propriétés peut être celui avec des valeurs "brutes" c'est-à-dire qu'elles peuvent
 * aussi contenir un callback retournant une string, par exemple, lorsqu'on veut rendre la valeur dynamiquement.
 * Règle générale, le contrôleur principal gérera l'objet avec les valeurs brutes et retournera l'objet avec les
 * valeurs filtrées et traduites aux composantes descendantes.
 */
export type BookingFormStepper<StepName extends string = string, Raw extends boolean = false> = Record<StepName, Raw extends true ? BookingFormStepperPropsRaw : BookingFormStepperProps>;

export type FormStepperItemCallbackOptions = {
  t: ComposerTranslation;
};

/** Propriétés communes passées à tous les formulaires multi-pages */
interface BookingFormStepperPropsBase {
  /** L'étape est activée ou non */
  enabled?: boolean;
}

/**
 * Fonction de transformation d'une valeur de propriété à l'intérieur de l'objet de configuration des étapes.
 */
type FormStepperPropCallback<T = unknown> = (data: T, options: FormStepperItemCallbackOptions) => string;

/**
 * Objet de valeurs "brutes" définissant chaque étape (chaque formulaire) à l'intérieur du formulaire global de demande
 * de réservation.
 *
 * On entend par "brutes" des valeurs qui ne sont pas finales/prête pour l'affichage, c'est-à-dire qu'il peut y avoir
 * un traitement à effectuer dessus par l'intermédiaire d'un callback function qui retournera alors la valeur finale.
 *
 * Exemple: le titre de certaines étapes devant être pluralisé en fonction d'une valeur précédente sélectionnée dans
 * le formulaire.
 *
 * Règle générale, le contrôleur principal gérera l'objet avec les valeurs brutes et retournera l'objet avec les
 * valeurs filtrées et traduites aux composantes descendantes.
 */
export interface BookingFormStepperPropsRaw<T = unknown> extends BookingFormStepperPropsBase {
  items: FormItemConfigRaw<T>[];
  title: string | FormStepperPropCallback<T>;
}

/**
 * Objet de valeurs "finalisé" définissant chaque étape (chaque formulaire) à l'intérieur du formulaire global de
 * demande de réservation. Par "finalisé" on signifie que les étapes ont été filtrées en fonction des paramètres de
 * l'utilisateur et que les traductions ont été effectuées.
 *
 * Règle générale, le contrôleur principal du formulaire convertit l'objet d'étapes brutes en objet "finalisé", puis
 * cet objet est celui qui sera utilisé/passé dans tous les formulaires enfants au besoin.
 */
export interface BookingFormStepperProps extends BookingFormStepperPropsBase {
  /**
   * items: liste d'éléments, chacun d'eux étant un item (question) du formulaire.
   * Les erreurs affichées dans la boîte de statut est basée sur cette liste pour ouvrir la bonne page et pointer le
   * bon élément lors d'un clic sur le lien.
   */
  items: FormItemConfigRaw[];
  /** Titre du formulaire, utilisé notamment pour la navigation. */
  title: string;
}

/**
 * Configuration de données pour chaque formulaire de réservation et destiné à la construction du payload pour la
 * validation/soumission.
 */
export type IBookingForms =
  | IBookingFormDated
  | IBookingFormTemplate;

/** Interface de retour utilisé lors de la création d'une nouvelle instance "useStepper" */
export type BookingFormStepperReturn<TStep extends string> = UseStepperReturn<
  TStep,
  BookingFormStepper<TStep>,
  BookingFormStepperProps
>;

export type StepControllerProps<TForm extends AnyObject = AnyObject, TStepData = never> = {
  id: string;
  onSubmit: FormSubmissionHandler<TForm>;
  stepItems: FormItemConfigRaw<TStepData>[];
  submitting: boolean;
  /** Valeurs initiales à utiliser dans le formulaire. Possiblement non-défini dans le cas d'une nouvelle demande */
  initialValues: TForm | undefined;
  /**
   * Affiche ou non les boutons "validation/soumettre" et "précédent" dans le formulaire de réservation.
   * Certains cas spécifiques, comme dans les formulaires de changement d'heure après la soumission effectuée
   * avec succès, requièrent de ne pas afficher les boutons réguliers du formulaires car d'autres boutons spécifiques
   * sont utilisés pour la soumission.
   * @default true
   */
  showFormButtons?: boolean;
};

export type TripDirection = 'inward' | 'outward';

/** Objet définissant les types de valeurs pour un trajet aller-retour, */
export type RoundTripValue<TVal> = Record<TripDirection, TVal>;

/**
 * État "sommaire" du formulaire défini en fonction des actions présentes et/ou précédentes de l'utilisateur.
 * Ces états permettent d'effectuer des actions spécifiques soit globalement, soit dans une étape.
 *
 * - "New": Nouveau formulaire, non-modifié par un rechargement, une revue, etc.
 * - "Reload": Le formulaire a été rechargé et l'état actuelle n'a pas encore été validée.  Une fois validée avec
 *    succès, l'état global passe au statut "resume".
 * - Resume: Le déroulement normal des étapes suit son cours après une action intermédiaire (ex. un rechargement).
 * retourner à la première étape.
 * */
export enum FormMode {
  /** Nouveau formulaire de réservation */
  Creation = 'creation',
  /** Formulaire de réservation a été rechargé */
  Reload = 'reload',
  /** Modification d'une requête de service existant (aller simple ou aller-retour) */
  Modification = 'modification',
  /** Modification d'un segment dans une requête de service existante */
  LegModification = 'legModification',
  /** Ajout d'un nouveau segment à la demande de déplacement existante */
  LegCreation = 'legCreation',
  /** Duplication d'un déplacement existant */
  Duplicate = 'duplicate',
}

/** paramètres d'url possible définissant l'état d'affichage du formulaire */
export type FormActionQuery = 'soumission' | 'edition';

export type {
  FormSubmissionHandler,
  FormItemConfigRaw,
  FormItemConfig,
};
